import {ResourceList} from "./resource-list";
import {BookmarkHelpers} from "../helpers/bookmark-helpers";
import {Modal} from "../modal";

/**
 * Handles all portal resource list page logic:
 * bookmarks, hero search, and share-selected.
 */
export class PortalList {
	private readonly list: ResourceList;
	private shareModal: Modal;

	/** Domain that qualifies a recipient as "internal" and therefore allowed to receive internal-only files. */
	private static readonly INTERNAL_EMAIL_DOMAIN = 'cmc.com';

	constructor() {
		this.list = new ResourceList(`.portal-list-widget`, null, 12, "/portal/search");

		const portalList = document.querySelector('.portal-list') as HTMLElement | null;
		if (portalList?.dataset.bookmarksOnly === 'true') {
			this.list.bookmarksOnly = true;
		}

		this.initBookmarks();
		this.initHeroSearch();
		this.initSortDropdown();
		this.initBookmarkTriggers();
		this.initCopyUrl();
		this.initCardLinkSelection();
		this.initShareModal();
	}

	/** Wire up the "sort by" dropdown for updated and added dates. */
	private initSortDropdown(): void {
		const sortSelect = document.querySelector('.portal-sort select') as HTMLSelectElement | null;
		if (!sortSelect) return;

		this.list.sortOrder = sortSelect.value;

		sortSelect.addEventListener('change', () => {
			this.list.sortOrder = sortSelect.value;
			this.list.resetToFirstPage();
			const filters = this.list.getSelectedFilters();
			this.list.search("/portal/search", filters, false);
		});
	}

	/** Fetch bookmarks once and mark server-rendered cards. */
	private initBookmarks(): void {
		BookmarkHelpers.fetchBookmarks().then(() => {
			document.querySelectorAll('.bookmark-trigger').forEach(trigger => {
				BookmarkHelpers.updateBookmarkUI(trigger as HTMLElement);
			});
		});
	}

	/** Wire up the hero search bar. */
	private initHeroSearch(): void {
		const searchBtn = document.querySelector('.portal-hero-search-btn') as HTMLElement | null;
		const searchText = document.querySelector('.portal-hero-search input[type="text"]') as HTMLInputElement;

		if (searchBtn && searchText) {
			searchBtn.addEventListener('click', (e) => {
				e.preventDefault();
				this.list.searchText = searchText.value;
				const filters = this.list.getSelectedFilters();
				this.list.search("/portal/search", filters, false);
			});

			searchText.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					searchBtn.click();
				}
			});
		}
	}

	/**
	 * Prevent the card link from navigating when the user finishes a text-selection
	 * drag inside the card (mouseup with selected text fires a click on the anchor).
	 */
	private initCardLinkSelection(): void {
		document.addEventListener('click', (e) => {
			const link = (e.target as Element).closest('.portal-resource-item > a');
			if (!link) return;

			const selection = window.getSelection();
			if (selection && selection.toString().length > 0) {
				e.preventDefault();
			}
		});
	}

	/** Copy the resource URL to the clipboard when a copy-url button is clicked. */
	private initCopyUrl(): void {
		document.addEventListener('click', (e) => {
			const btn = (e.target as Element).closest('.copy-url-trigger') as HTMLButtonElement | null;
			if (!btn) return;

			e.preventDefault();

			// Warn (but don't block) when copying a link to an internal-only document.
			const card = btn.closest('.portal-resource-item');
			if (card?.classList.contains('internal-only')) {
				const proceed = confirm(
					'⚠️ This document is marked as internal only.\n\n' +
					'Please ensure this link is only shared with authorized internal recipients.'
				);
				if (!proceed) return;
			}

			const rawUrl = btn.dataset.copyUrl ?? '';
			if (!rawUrl) return;

			// Resolve relative paths to a full absolute URL.
			const absoluteUrl = rawUrl.startsWith('http://') || rawUrl.startsWith('https://')
				? rawUrl
				: new URL(rawUrl, window.location.origin).href;

			const fallbackCopy = (text: string): boolean => {
				const ta = document.createElement('textarea');
				ta.value = text;
				ta.style.position = 'fixed';
				ta.style.opacity = '0';
				document.body.appendChild(ta);
				ta.focus();
				ta.select();
				let ok = false;
				try { ok = document.execCommand('copy'); } catch (_) { /* unsupported */ }
				document.body.removeChild(ta);
				return ok;
			};

			const onCopyFailed = () => {
				this.showCopyBlockedFeedback(btn, absoluteUrl);
			};

			if (navigator.clipboard?.writeText) {
				navigator.clipboard.writeText(absoluteUrl).then(() => {
					this.showCopiedFeedback(btn);
				}).catch(() => {
					if (fallbackCopy(absoluteUrl)) {
						this.showCopiedFeedback(btn);
					} else {
						onCopyFailed();
					}
				});
			} else if (fallbackCopy(absoluteUrl)) {
				this.showCopiedFeedback(btn);
			} else {
				onCopyFailed();
			}
		});
	}

	/** Briefly swap the button label to "Copied!" then restore. */
	private showCopiedFeedback(btn: HTMLButtonElement): void {
		if (btn.classList.contains('copied')) return;

		const labelEl = btn.querySelector('.label');
		const originalText = labelEl?.textContent ?? 'Copy Link';

		btn.classList.add('copied');
		if (labelEl) labelEl.textContent = 'Copied!';

		setTimeout(() => {
			btn.classList.remove('copied');
			if (labelEl) labelEl.textContent = originalText;
		}, 2000);
	}

	/** Show a prompt with the URL when clipboard access is blocked by the browser. */
	private showCopyBlockedFeedback(btn: HTMLButtonElement, url: string): void {
		window.prompt(
			'Your browser is blocking clipboard access. You can copy the link manually from below:',
			url
		);
	}

	/** Toggle bookmarks on click — delegated so it works on dynamically loaded cards. */
	private initBookmarkTriggers(): void {
		document.addEventListener('click', (e) => {
			const el = (e.target as Element).closest('.bookmark-trigger') as HTMLElement | null;
			if (!el) return;

			e.preventDefault();

			// Ignore clicks while a request is already in flight
			if (el.classList.contains('loading')) return;

			const identifier = el.dataset.identifier;
			const isSharepoint = el.dataset.issharepoint;
			if (!identifier) return;

			const isCurrentlyBookmarked = el.classList.contains('active');
			const url = isCurrentlyBookmarked
				? `/api/bookmarks/remove?fileIdentifier=${encodeURIComponent(identifier)}`
				: `/api/bookmarks/add?fileIdentifier=${encodeURIComponent(identifier)}&isSharepoint=${isSharepoint}`;

			// Show loading spinner, stash original icon HTML
			const iconEl = el.querySelector('.icon');
			const originalIconHTML = iconEl ? iconEl.innerHTML : null;
			el.classList.add('loading');
			if (iconEl) iconEl.innerHTML = '<span class="bookmark-spinner"></span>';

			fetch(url).then(response => {
				if (response.ok) {
					const nowBookmarked = !isCurrentlyBookmarked;
					if (nowBookmarked) {
						BookmarkHelpers.addToCache(identifier);
					} else {
						BookmarkHelpers.removeFromCache(identifier);
					}
					BookmarkHelpers.updateBookmarkUI(el, nowBookmarked);
				}
			}).finally(() => {
				el.classList.remove('loading');
				if (iconEl && originalIconHTML !== null) iconEl.innerHTML = originalIconHTML;
			});
		});
	}

	/** Resolve selected resources (name + originating checkbox) from the card container. */
	private getSelectedFiles(checked: NodeListOf<HTMLInputElement>): { name: string; checkbox: HTMLInputElement; isInternalOnly: boolean }[] {
		return Array.from(checked)
			.map(cb => {
				const name = cb.closest('.portal-resource-item')?.querySelector('h3')?.textContent?.trim();
				return name
					? { name, checkbox: cb, isInternalOnly: cb.dataset.isinternalonly === 'true' }
					: null;
			})
			.filter((entry): entry is { name: string; checkbox: HTMLInputElement; isInternalOnly: boolean } => entry !== null);
	}

	/** Returns true when the email belongs to the internal CMC domain. */
	private isInternalEmail(email: string): boolean {
		const at = email.lastIndexOf('@');
		if (at < 0) return false;
		return email.slice(at + 1).trim().toLowerCase() === PortalList.INTERNAL_EMAIL_DOMAIN;
	}

	/** Split the comma/semicolon-separated recipient input into individual trimmed addresses. */
	private parseRecipients(raw: string): string[] {
		return raw
			.split(/[,;\n\r]+/)
			.map(e => e.trim())
			.filter(e => e.length > 0);
	}

	/** Render a readable list of selected files in the share modal. */
	private renderSelectedFiles(countEl: Element | null, files: { name: string; checkbox: HTMLInputElement; isInternalOnly: boolean }[]): void {
		if (!countEl) return;

		countEl.replaceChildren();

		if (files.length === 0) {
			return;
		}

		const intro = document.createElement('p');
		intro.textContent = `${files.length} file${files.length > 1 ? 's' : ''} selected:`;

		const list = document.createElement('ul');
		list.className = 'share-selected-files';

		files.forEach(file => {
			const item = document.createElement('li');
			if (file.isInternalOnly) {
				item.classList.add('internal-only');
			}

			const label = document.createElement('span');
			label.className = 'share-selected-file-name';
			label.textContent = file.name;

			if (file.isInternalOnly) {
				const badge = document.createElement('span');
				badge.className = 'share-selected-internal-badge';
				badge.textContent = ' Internal only';
				label.appendChild(badge);
			}

			const removeBtn = document.createElement('button');
			removeBtn.type = 'button';
			removeBtn.className = 'share-selected-remove';
			removeBtn.setAttribute('aria-label', `Remove ${file.name}`);
			removeBtn.title = 'Remove';
			removeBtn.innerHTML = '&times;';
			removeBtn.addEventListener('click', (e) => {
				e.preventDefault();
				file.checkbox.checked = false;
				file.checkbox.dispatchEvent(new Event('change', { bubbles: true }));

				const remaining = document.querySelectorAll('.portal-select input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>;
				if (remaining.length === 0) {
					this.shareModal.close();
					return;
				}
				this.renderSelectedFiles(countEl, this.getSelectedFiles(remaining));
			});

			item.appendChild(label);
			item.appendChild(removeBtn);
			list.appendChild(item);
		});

		countEl.appendChild(intro);
		countEl.appendChild(list);
	}

	/** Initialise the share modal and wire up open / cancel / submit. */
	private initShareModal(): void {
		const shareBtn = document.querySelector('.portal-share-button');
		if (!shareBtn || !document.querySelector('#portal-share-modal')) return;

		this.shareModal = new Modal('', '#portal-share-modal');

		const form = document.getElementById('portal-share-form') as HTMLFormElement;
		const emailInput = document.getElementById('share-to-email') as HTMLInputElement;
		const messageInput = document.getElementById('share-message') as HTMLTextAreaElement;
		const countEl = this.shareModal.findElement('.share-selected-count');
		const cancelBtn = this.shareModal.findElement('.share-cancel');
		const warningEl = this.shareModal.findElement('.share-internal-warning') as HTMLElement | null;

		/**
		 * Show/hide the "external recipient + internal-only files" warning, based on the current
		 * selection and the recipient email(s) entered in the modal.
		 */
		const updateInternalWarning = () => {
			if (!warningEl) return;
			const selected = document.querySelectorAll('.portal-select input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>;
			const internalCount = Array.from(selected).filter(cb => cb.dataset.isinternalonly === 'true').length;
			const recipients = this.parseRecipients(emailInput.value);
			const externalRecipients = recipients.filter(r => !this.isInternalEmail(r));

			if (internalCount > 0 && externalRecipients.length > 0) {
				warningEl.textContent =
					`Note: ${internalCount} of the selected file${internalCount > 1 ? 's are' : ' is'} marked internal only. ` +
					`Only @${PortalList.INTERNAL_EMAIL_DOMAIN} recipients will receive those file${internalCount > 1 ? 's' : ''}; ` +
					`the ${externalRecipients.length === 1 ? 'external recipient' : `${externalRecipients.length} external recipients`} will only get the remaining items.`;
				warningEl.hidden = false;
				warningEl.classList.remove('d-none');
			} else {
				warningEl.textContent = '';
				warningEl.hidden = true;
				warningEl.classList.add('d-none');
			}
		};

		emailInput.addEventListener('input', updateInternalWarning);

		// Toggle the button's active state whenever a checkbox changes (delegated, handles dynamic cards)
		const updateShareBtnState = () => {
			const anyChecked = document.querySelectorAll('.portal-select input[type="checkbox"]:checked').length > 0;
			shareBtn.classList.toggle('active', anyChecked);
		};

		document.addEventListener('change', (e) => {
			const target = e.target as HTMLElement;
			if (target && target.matches('.portal-select input[type="checkbox"]')) {
				updateShareBtnState();
			}
		});

		// Open modal when share button is clicked
		shareBtn.addEventListener('click', (e) => {
			e.preventDefault();

			const checked = document.querySelectorAll('.portal-select input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>;
			if (checked.length === 0) {
				alert('Please select at least one item to share.');
				return;
			}

			// Reset form and show selected resource names.
			form.reset();
			const files = this.getSelectedFiles(checked);
			this.renderSelectedFiles(countEl, files);
			updateInternalWarning();
			this.shareModal.open(e);
		});

		// Cancel button closes modal
		cancelBtn?.addEventListener('click', (e) => {
			e.preventDefault();
			this.shareModal.close();
		});

		// Form submit sends the share request
		form.addEventListener('submit', (e) => {
			e.preventDefault();

			const toEmail = emailInput.value.trim();
			if (!toEmail) return;

			const recipients = this.parseRecipients(toEmail);
			if (recipients.length === 0) {
				emailInput.focus();
				return;
			}

			const message = messageInput.value.trim();
			const checked = document.querySelectorAll('.portal-select input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>;
			const files = Array.from(checked).map(cb => ({
				identifier: cb.value,
				isSharePointFile: cb.dataset.issharepoint === 'true',
				isInternalOnly: cb.dataset.isinternalonly === 'true'
			}));

			const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
			submitBtn.disabled = true;
			submitBtn.textContent = 'Sending...';

			fetch('/api/portal/share', {
				method: 'POST',
				credentials: 'same-origin',
				headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({toEmail, message, files})
				}).then(response => {
					if (response.ok) {
						checked.forEach(cb => cb.checked = false);
						updateShareBtnState();
						this.showSubmitBtnSentState(submitBtn);
					} else {
						alert('Failed to share resources. Please try again.');
					}
				}).finally(() => {
					submitBtn.disabled = false;
				});
		});
	}

	/** Briefly show a "Sent" success state on the modal submit button, then close. */
	private showSubmitBtnSentState(btn: HTMLButtonElement): void {
		btn.innerHTML = '✓ Sent';
		btn.classList.add('sent');

		setTimeout(() => {
			this.shareModal.close();
			btn.innerHTML = 'Send';
			btn.classList.remove('sent');
		}, 1500);
	}
}
